import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { medicalPOAPreferencesService } from '../../../../services/medicalPOAPreferencesInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MedicalPOAPreferencesInput');

type MedicalPOAPreferencesInputScreenRouteProp = RouteProp<RootStackParamList, 'MedicalPOAPreferencesInput'>;

export const MedicalPOAPreferencesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<MedicalPOAPreferencesInputScreenRouteProp>();

  const { medicalPOAPreferencesData, isEditing } = route.params || {};

  // Primary Agent Fields
  const [primaryAgentName, setPrimaryAgentName] = useState(
    medicalPOAPreferencesData?.primaryAgentName || ''
  );
  const [primaryAgentRelationship, setPrimaryAgentRelationship] = useState(
    medicalPOAPreferencesData?.primaryAgentRelationship || ''
  );
  const [primaryAgentPhone, setPrimaryAgentPhone] = useState(
    medicalPOAPreferencesData?.primaryAgentPhone || ''
  );
  const [primaryAgentEmail, setPrimaryAgentEmail] = useState(
    medicalPOAPreferencesData?.primaryAgentEmail || ''
  );

  // Alternate Agent Fields
  const [alternateAgentName, setAlternateAgentName] = useState(
    medicalPOAPreferencesData?.alternateAgentName || ''
  );
  const [alternateAgentRelationship, setAlternateAgentRelationship] = useState(
    medicalPOAPreferencesData?.alternateAgentRelationship || ''
  );
  const [alternateAgentPhone, setAlternateAgentPhone] = useState(
    medicalPOAPreferencesData?.alternateAgentPhone || ''
  );
  const [alternateAgentEmail, setAlternateAgentEmail] = useState(
    medicalPOAPreferencesData?.alternateAgentEmail || ''
  );

  // Other Fields
  const [documentLocation, setDocumentLocation] = useState(
    medicalPOAPreferencesData?.documentLocation || ''
  );
  const [attorneyContact, setAttorneyContact] = useState(
    medicalPOAPreferencesData?.attorneyContact || ''
  );
  const [familyInformed, setFamilyInformed] = useState(
    medicalPOAPreferencesData?.familyInformed || false
  );
  const [specificInstructions, setSpecificInstructions] = useState(
    medicalPOAPreferencesData?.specificInstructions || ''
  );
  const [saving, setSaving] = useState(false);

  // Phone number formatting function
  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const numbers = text.replace(/\D/g, '');

    // Format as (xxx) xxx-xxxx
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSave = async () => {
    // Validation
    if (!primaryAgentName.trim()) {
      Alert.alert('Error', 'Please enter the primary agent name');
      return;
    }

    if (!primaryAgentRelationship.trim()) {
      Alert.alert('Error', 'Please enter the primary agent relationship');
      return;
    }

    if (!primaryAgentPhone.trim()) {
      Alert.alert('Error', 'Please enter the primary agent phone number');
      return;
    }

    if (!primaryAgentEmail.trim()) {
      Alert.alert('Error', 'Please enter the primary agent email');
      return;
    }

    if (!isValidEmail(primaryAgentEmail)) {
      Alert.alert('Error', 'Please enter a valid email address for the primary agent');
      return;
    }

    if (alternateAgentEmail && !isValidEmail(alternateAgentEmail)) {
      Alert.alert('Error', 'Please enter a valid email address for the alternate agent');
      return;
    }

    if (!documentLocation.trim()) {
      Alert.alert('Error', 'Please enter the document location');
      return;
    }

    try {
      setSaving(true);

      const medicalPOAPreferencesUpdateData = {
        primaryAgentName: primaryAgentName.trim(),
        primaryAgentRelationship: primaryAgentRelationship.trim(),
        primaryAgentPhone: primaryAgentPhone.trim(),
        primaryAgentEmail: primaryAgentEmail.trim(),
        alternateAgentName: alternateAgentName.trim(),
        alternateAgentRelationship: alternateAgentRelationship.trim(),
        alternateAgentPhone: alternateAgentPhone.trim(),
        alternateAgentEmail: alternateAgentEmail.trim(),
        documentLocation: documentLocation.trim(),
        attorneyContact: attorneyContact.trim(),
        familyInformed,
        lastUpdated: new Date().toISOString(),
        specificInstructions: specificInstructions.trim(),
      };

      let savedMedicalPOAPreferences;

      if (isEditing && medicalPOAPreferencesData?.id) {
        // Update existing record
        savedMedicalPOAPreferences = await medicalPOAPreferencesService.updateMedicalPOAPreferences(
          medicalPOAPreferencesData.id,
          medicalPOAPreferencesUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedMedicalPOAPreferences?.data || savedMedicalPOAPreferences;
        logger.debug('Medical POA preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('MedicalPOAPreferencesLockUnlock', { medicalPOAPreferencesId: updatedData.id });
      } else {
        // Create new record
        savedMedicalPOAPreferences = await medicalPOAPreferencesService.createMedicalPOAPreferences(medicalPOAPreferencesUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedMedicalPOAPreferences?.data || savedMedicalPOAPreferences;
        logger.debug('Medical POA preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('MedicalPOAPreferencesLockUnlock', { medicalPOAPreferencesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving medical POA preferences:', error);
      Alert.alert('Error', 'Failed to save medical POA preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Medical Power of Attorney
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          {/* Primary Agent Section */}
          <Text style={styles.sectionTitle}>Primary Healthcare Agent:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={primaryAgentName}
              onChangeText={setPrimaryAgentName}
              placeholder="Enter full name of primary agent"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship:</Text>
            <TextInput
              style={styles.input}
              value={primaryAgentRelationship}
              onChangeText={setPrimaryAgentRelationship}
              placeholder="e.g., Spouse, Adult Child, Sibling"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number:</Text>
            <TextInput
              style={styles.input}
              value={primaryAgentPhone}
              onChangeText={(text) => setPrimaryAgentPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email Address:</Text>
            <TextInput
              style={styles.input}
              value={primaryAgentEmail}
              onChangeText={setPrimaryAgentEmail}
              placeholder="Enter email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          {/* Alternate Agent Section */}
          <Text style={styles.sectionTitle}>Alternate Healthcare Agent (Optional):</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={alternateAgentName}
              onChangeText={setAlternateAgentName}
              placeholder="Enter full name of alternate agent"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship:</Text>
            <TextInput
              style={styles.input}
              value={alternateAgentRelationship}
              onChangeText={setAlternateAgentRelationship}
              placeholder="e.g., Spouse, Adult Child, Sibling"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number:</Text>
            <TextInput
              style={styles.input}
              value={alternateAgentPhone}
              onChangeText={(text) => setAlternateAgentPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email Address:</Text>
            <TextInput
              style={styles.input}
              value={alternateAgentEmail}
              onChangeText={setAlternateAgentEmail}
              placeholder="Enter email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          {/* Document Location Input */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Document Location:</Text>
            <TextInput
              style={styles.input}
              value={documentLocation}
              onChangeText={setDocumentLocation}
              placeholder="Where is your medical POA document stored?"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Attorney Contact Input */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Attorney Contact:</Text>
            <TextInput
              style={styles.input}
              value={attorneyContact}
              onChangeText={setAttorneyContact}
              placeholder="Attorney or legal advisor contact information"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Family Informed Toggle */}
          <View style={styles.toggleRow}>
            <Text style={styles.toggleLabel}>Have you informed your family about your medical POA?</Text>
            <Switch
              value={familyInformed}
              onValueChange={setFamilyInformed}
              trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
              thumbColor={familyInformed ? '#FFFFFF' : '#FFFFFF'}
            />
          </View>

          {/* Special Instructions Input */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specificInstructions}
              onChangeText={setSpecificInstructions}
              placeholder="Enter any additional special instructions for your healthcare agents"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(16),
    marginTop: ms(10),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(20),
    paddingVertical: ms(10),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    flex: 1,
    marginRight: ms(15),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});